// WriteVocabulary.js
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useFailedQuestions } from './FailedQuestionsContext';
import { saveQuizScore, updateStudyStreak } from '../utils/storage';
import BackButton from '../components/BackButton';
import Icon from '../components/Icon';
import './styles/WriteVocabulary.css';
import vocabulary_A1 from '../data/vocabulary_A1';
import vocabulary_A2 from '../data/vocabulary_A2';
import vocabulary_B1 from '../data/vocabulary_B1';
import vocabulary_B2 from '../data/vocabulary_B2';
import extra_vocabulary from '../data/extra_vocabulary';

const WriteVocabulary = () => {
  const location = useLocation();
  const { totalQuestions, selectedCategories } = location.state || {};
  const { failedWritings, setFailedWritings } = useFailedQuestions();

  const categoryToVocabulary = {
    A1: vocabulary_A1, A2: vocabulary_A2,
    B1: vocabulary_B1, B2: vocabulary_B2,
    extras: extra_vocabulary, failed: failedWritings,
  };

  const vocabularyData = selectedCategories.reduce((data, categoryId) => {
    const cat = categoryToVocabulary[categoryId];
    if (cat) data.push(...cat);
    return data;
  }, []);

  const generateQuestions = (vocab, n) => {
    const out = [];
    const used = new Set();
    if (n > vocab.length) n = vocab.length;
    while (out.length < n) {
      const i = Math.floor(Math.random() * vocab.length);
      if (!used.has(i)) {
        used.add(i);
        out.push({ question: vocab[i].question, answer: vocab[i].answer });
      }
    }
    return out;
  };

  const storeFailedQuestions = (fq) => {
    try { localStorage.setItem('failedWritings', JSON.stringify(fq)); } catch {}
  };

  const getFailedQuestions = async () => {
    try {
      const d = localStorage.getItem('failedWritings');
      return d ? JSON.parse(d) : [];
    } catch { return []; }
  };

  useEffect(() => {
    getFailedQuestions().then(setFailedWritings);
  }, [setFailedWritings]);

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers]     = useState([]);
  const [results, setResults]     = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [bankOpen, setBankOpen]   = useState(true);

  useEffect(() => {
    if (questions.length === 0) {
      const q = generateQuestions(vocabularyData, totalQuestions);
      if (q.length > 0) {
        setQuestions(q);
        setAnswers(Array(q.length).fill(''));
      }
    }
  }, [questions, vocabularyData, totalQuestions]); // eslint-disable-line

  // Shuffle the word bank once when questions are ready
  const shuffledBank = useMemo(() => {
    if (!questions.length) return [];
    return [...questions.map(q => q.question)].sort(() => Math.random() - 0.5);
  }, [questions.length]); // eslint-disable-line

  const handleAnswerChange = (index, text) => {
    const next = [...answers];
    next[index] = text;
    setAnswers(next);
  };

  const handleSubmit = () => {
    const newResults = answers.map((answer, index) => {
      if (!questions[index]) return false;
      const actual    = questions[index].question.trim().toLowerCase();
      const submitted = (answer || '').trim().toLowerCase();
      const isCorrect = submitted === actual;
      const key = questions[index].question.trim().toLowerCase();
      setFailedWritings(prev => prev.filter(f => f.question.trim().toLowerCase() !== key));
      if (!isCorrect) setFailedWritings(prev => [...prev, questions[index]]);
      return isCorrect;
    });
    setResults(newResults);
    saveQuizScore('Write Vocabulary', newResults.filter(Boolean).length, questions.length);
    updateStudyStreak();
    storeFailedQuestions(failedWritings);
  };

  const handleClear = () => {
    setAnswers(Array(questions.length).fill(''));
    setResults([]);
    setSubmitted(false);
  };

  const speakNow = (text) => {
    const synth = window.speechSynthesis;
    synth.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'fi-FI';
    synth.speak(u);
  };

  const correctCount = results.filter(Boolean).length;

  if (questions.length === 0) {
    return (
      <div className="container">
        <BackButton to="/vocabulary" label="Vocabulary" />
        <p className="noQuestionsText">No questions available</p>
      </div>
    );
  }

  return (
    <div className="container">
      <BackButton to="/vocabulary" label="Vocabulary" />
      <h2 className="page-title">Write Vocabulary</h2>

      {/* ── Word Bank ── */}
      <div className="wv-bank">
        <button className="wv-bank-header" onClick={() => setBankOpen(o => !o)}>
          <span className="wv-bank-label">
            <Icon name="bulb" size={15} />
            Word Bank
            <span className="wv-bank-count">{questions.length} words</span>
          </span>
          <Icon name={bankOpen ? 'cross' : 'more'} size={14} />
        </button>
        {bankOpen && (
          <div className="wv-bank-chips">
            {shuffledBank.map((word, i) => {
              const matchIdx = questions.findIndex(q => q.question === word);
              const done = results[matchIdx] === true ||
                (matchIdx !== -1 && (answers[matchIdx] || '').trim().toLowerCase() === word.trim().toLowerCase());
              return (
                <button
                  key={i}
                  className={`wv-chip${done ? ' wv-chip-done' : ''}`}
                  onClick={() => {
                    const el = document.getElementById(`wv-input-${matchIdx}`);
                    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
                  }}
                >
                  {word}
                  {done && <Icon name="check" size={12} color="var(--color-success)" strokeWidth={3} />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Questions ── */}
      <div className="wv-list">
        {questions.map(({ question, answer }, index) => {
          const isCorrect   = results[index] === true;
          const isWrong     = results[index] === false;
          const typed       = answers[index] || '';
          const liveCorrect = !submitted && typed.trim().toLowerCase() === question.trim().toLowerCase();

          return (
            <div key={question} className={`wv-item${(isCorrect || liveCorrect) ? ' wv-item-correct' : isWrong ? ' wv-item-wrong' : ''}`}>

              {/* Prompt */}
              <p className="wv-prompt">
                <span className="wv-index">{index + 1}</span>
                {answer}
              </p>

              {/* Letter boxes — clicking focuses the input */}
              <div
                className="wv-boxes-row"
                onClick={() => document.getElementById(`wv-input-${index}`)?.focus()}
                style={{ cursor: isCorrect ? 'default' : 'text' }}
              >
                <div className="wv-boxes">
                  {question.split('').map((char, ci) => {
                    const typedChar = typed[ci] || '';
                    const isSpace   = char === ' ';
                    if (isSpace) return <span key={ci} className="wv-box-space" />;
                    return (
                      <span
                        key={ci}
                        className={`wv-box${typedChar ? ' wv-box-filled' : ''}${(isCorrect || liveCorrect) ? ' wv-box-correct' : ''}${isWrong && submitted ? ' wv-box-wrong' : ''}`}
                      >
                        {typedChar}
                      </span>
                    );
                  })}
                </div>
                <span className="wv-letter-count">
                  {question.replace(/ /g, '').length} letters
                </span>
              </div>

              {/* Input */}
              <div className="wv-input-row">
                <input
                  id={`wv-input-${index}`}
                  className={`wv-input${(isCorrect || liveCorrect) ? ' wv-input-correct' : isWrong ? ' wv-input-wrong' : ''}`}
                  type="text"
                  placeholder="Type the Finnish word…"
                  value={typed}
                  onChange={e => handleAnswerChange(index, e.target.value)}
                  disabled={isCorrect}
                />
                {liveCorrect && (
                  <span className="wv-live-check" aria-label="Correct">
                    <Icon name="check" size={16} color="var(--color-success)" strokeWidth={2.5} />
                  </span>
                )}
                {(isCorrect || isWrong) && (
                  <button className="wv-speak-btn" onClick={() => speakNow(question)} aria-label="Hear pronunciation">
                    <Icon name="headphones" size={18} />
                  </button>
                )}
              </div>

              {/* Reveal after submit */}
              {submitted && (isWrong || isCorrect) && (
                <p className={`wv-reveal${isCorrect ? ' wv-reveal-correct' : ' wv-reveal-wrong'}`}>
                  {isCorrect
                    ? <><Icon name="check" size={14} /> {question}</>
                    : <><Icon name="cross" size={14} /> {question}</>
                  }
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Score ── */}
      {submitted && (
        <div className="wv-score">
          <span className="wv-score-num" style={{ color: correctCount / questions.length >= 0.7 ? 'var(--color-success)' : 'var(--color-error)' }}>
            {correctCount} / {questions.length}
          </span>
          <span className="wv-score-label">correct</span>
        </div>
      )}

      {/* ── Buttons ── */}
      <div className="buttonContainer">
        <button className="submitButton" onClick={() => { handleSubmit(); setSubmitted(true); }}>
          Submit
        </button>
        <button className="clearButton" onClick={handleClear}>
          Clear
        </button>
      </div>
    </div>
  );
};

export default WriteVocabulary;
